import { addDays, addYears } from '@if/core/date'
import { EMerchantType } from '@if/core/types'
import { ControlledCheckbox, ControlledMultipleSelect, ControlledSelect, ControlledDateOnlyPicker } from '@if/mui'
import { CircularProgress, Grid, Stack } from '@mui/material'
import { useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { Merchant } from '@src/types/Merchant'
import { useMemo } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ApplicationsFilters } from '../browseCreditApplication-hooks'
import { useCreditApplicationStatuses, useCurrentCustomerSteps } from './creditEntriesFiltersDialog-hooks'

interface Props {
  control: Control<ApplicationsFilters>
  merchant: Merchant | null
}

/**
 * The applications filter fields, rendered inside DataGridPage's filter dialog
 * (`filterForm.content`). The dialog shell, Apply/Reset buttons and submit are
 * owned by DataGridPage; this component only provides the inputs, bound to the
 * dialog's form via `control`.
 */
export const ApplicationsFilterFields = ({ control, merchant }: Props) => {
  const { t } = useTranslation()
  const [merchantsList, isGettingMerchants] = useMerchantByParentId(merchant?.id ?? '')
  const creditApplicationStatuses = useCreditApplicationStatuses()
  const currentCustomerSteps = useCurrentCustomerSteps()

  const endDate = useWatch({ control, name: 'endDate' })
  const minDate = useMemo(() => (endDate ? addYears(addDays(endDate, 2), -2) : undefined), [endDate])

  if (isGettingMerchants) {
    return (
      <div style={{ textAlign: 'center' }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <Stack spacing={2}>
      {merchant?.merchantTypeId === EMerchantType.Banner && (
        <ControlledMultipleSelect
          name="merchantIds"
          control={control}
          options={merchantsList.map((m) => ({ label: m.name, value: m.id }))}
          label={t('common.merchant')}
          fullWidth
        />
      )}
      <ControlledSelect
        name="creditApplicationStatus"
        control={control}
        options={creditApplicationStatuses}
        label={t('common.status')}
        disableEmptyOption
        fullWidth
      />
      <ControlledSelect
        name="currentCustomerStep"
        control={control}
        options={currentCustomerSteps}
        label={t('common.state')}
        emptyOptionLabel={t('common.all')}
        fullWidth
      />
      <Stack justifyContent="space-between" direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 1 }}>
        <Grid>
          <ControlledDateOnlyPicker
            name="startDate"
            control={control}
            label={t('common.startDate')}
            minDate={minDate}
            maxDate={new Date()}
          />
        </Grid>
        <Grid>
          <ControlledDateOnlyPicker label={t('common.endDate')} name="endDate" control={control} />
        </Grid>
      </Stack>
      <Stack>
        <ControlledCheckbox
          name="requiresMerchantAttention"
          control={control}
          label={t('common.requiresMerchantAttention')}
        />
        <ControlledCheckbox
          name="onlyEditableApplications"
          control={control}
          label={t('common.editableApplications')}
        />
        <ControlledCheckbox
          name="onlyCurrentUserApplications"
          control={control}
          label={t('common.currentUserApplications')}
        />
      </Stack>
    </Stack>
  )
}
